/**
 * Страница настроек Telegram бота: управление автоответами (CRUD)
 * Позволяет создавать правила с триггерами (keywords/regex) и приоритетами
 * Дата: 29.12.2025 - Добавлен TenantSelector для SuperAdmin
 * Дата: 26.01.2026 - Добавлено frontend логирование (TODO-034)
 * Дата: 30.01.2026 - Рефакторинг под унифицированные UI компоненты (TODO-045)
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/api/bot.ts - API для работы с настройками бота
 * - components/ui/EmptyState.tsx - унифицированное пустое состояние
 * - components/ui/LoadingState.tsx - унифицированная загрузка
 * 
 * КАК РАБОТАЕТ:
 * - Отображает список правил автоответов
 * - Создание/редактирование/удаление правил
 * - Drag-n-drop для изменения порядка (через GripVertical)
 * 
 * СХЕМА:
 * BotSettingsPage → botApi → backend API
 */
import { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { botApi } from '@/api/bot';
import { AutoReplyRule } from '@/api/types';
import { Button, Input, Card, Badge, Modal, TenantSelector, EmptyState, LoadingState } from '@/components/ui';
import { MainLayout } from '@/components/layout/MainLayout';
import { LinearScreen } from '@/features/linear-canvas/LinearScreen';
import { linearCanvas } from '@/features/linear-canvas/linearCanvas';
import { useTenantScope } from '@/hooks/useTenantScope';
import { Plus, Edit2, Trash2, GripVertical } from 'lucide-react';

interface AutoReplyModalProps {
  rule: AutoReplyRule;
  onSave: (rule: AutoReplyRule) => void;
  onClose: () => void;
  isLoading: boolean;
}

function AutoReplyModal({ rule, onSave, onClose, isLoading }: AutoReplyModalProps) {
  const [form, setForm] = useState(rule);
  const [triggerInput, setTriggerInput] = useState('');
  
  const addTrigger = () => {
    if (triggerInput.trim()) {
      setForm({ ...form, triggers: [...form.triggers, triggerInput.trim()] });
      setTriggerInput('');
    }
  };
  
  const removeTrigger = (index: number) => {
    setForm({ ...form, triggers: form.triggers.filter((_, i) => i !== index) });
  };
  
  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={rule.id ? 'Редактировать автоответ' : 'Новый автоответ'}
      testId="modal-auto-reply"
      size="lg"
      footer={
        <div className="flex gap-2">
          <Button onClick={onClose} variant="secondary" className="flex-1">
            Отмена
          </Button>
          <Button 
            onClick={() => onSave(form)} 
            isLoading={isLoading}
            className="flex-1"
            testId="btn-save-auto-reply"
          >
            Сохранить
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Название</label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Приветствие"
            testId="input-rule-name"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">
            Ключевые слова (триггеры)
          </label>
          <div className="flex gap-2 mb-2">
            <Input
              value={triggerInput}
              onChange={(e) => setTriggerInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addTrigger()}
              placeholder="привет, здравствуйте..."
              testId="input-trigger"
            />
            <Button onClick={addTrigger} variant="secondary" testId="btn-add-trigger">+</Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {form.triggers.map((trigger, i) => (
              <span 
                key={i} 
                className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm flex items-center gap-1"
                data-testid={`trigger-tag-${trigger}`}
              >
                {trigger}
                <button onClick={() => removeTrigger(i)} className="hover:text-blue-900">×</button>
              </span>
            ))}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_regex}
            onChange={(e) => setForm({ ...form, is_regex: e.target.checked })}
            id="is_regex"
          />
          <label htmlFor="is_regex" className="text-sm">
            Использовать регулярные выражения
          </label>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">Текст ответа</label>
          <textarea
            value={form.response}
            onChange={(e) => setForm({ ...form, response: e.target.value })}
            className="input min-h-[100px]"
            placeholder="👋 Здравствуйте! Чем могу помочь?"
            data-testid="textarea-response"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">
            Приоритет (выше = раньше проверяется)
          </label>
          <Input
            type="number"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
            min={0}
            max={100}
          />
        </div>
        
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            id="is_active"
          />
          <label htmlFor="is_active" className="text-sm">Активно</label>
        </div>
      </div>
    </Modal>
  );
}

export function BotSettingsPage() {
  const queryClient = useQueryClient();
  const [editingRule, setEditingRule] = useState<AutoReplyRule | null>(null);
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();
  const effectiveTenantId = tenantId || '';
  
  // Список правил (с учётом выбранного tenant)
  const { data, isLoading } = useQuery({
    queryKey: ['auto-replies', effectiveTenantId],
    queryFn: () => botApi.getAutoReplies(effectiveTenantId),
    enabled: !!effectiveTenantId, // Не запрашиваем без tenant
  });
  
  const rules = data?.rules || [];
  
  // Создание/обновление правила (с учётом tenant)
  const saveMutation = useMutation({
    mutationFn: (rule: AutoReplyRule) => {
      if (rule.id) {
        logger.info('bot_settings_update_click', { rule_id: rule.id });
        return botApi.updateAutoReply(rule.id, rule, effectiveTenantId);
      } else {
        logger.info('bot_settings_create_click', { rule_name: rule.name });
        return botApi.createAutoReply(rule, effectiveTenantId);
      }
    },
    onSuccess: (data, rule) => {
      if (rule.id) {
        logger.info('bot_settings_updated', { rule_id: rule.id });
      } else {
        logger.info('bot_settings_created', { rule_id: data.id, rule_name: data.name });
      }
      queryClient.invalidateQueries({ queryKey: ['auto-replies', effectiveTenantId] });
      setEditingRule(null);
    },
    onError: (error: any, rule) => {
      logger.error('bot_settings_save_failed', { 
        rule_id: rule.id || 'new',
        error: error.response?.data?.error || error.message 
      });
    },
  });
  
  // Удаление правила (с учётом tenant)
  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      logger.info('bot_settings_delete_click', { rule_id: id });
      return botApi.deleteAutoReply(id, effectiveTenantId);
    },
    onSuccess: (_, id) => {
      logger.info('bot_settings_deleted', { rule_id: id });
      queryClient.invalidateQueries({ queryKey: ['auto-replies', effectiveTenantId] });
    },
    onError: (error: any, id) => {
      logger.error('bot_settings_delete_failed', { 
        rule_id: id,
        error: error.response?.data?.error || error.message 
      });
    },
  });

  // Логирование просмотра страницы
  useEffect(() => {
    logger.info('page_view', { page: 'bot-settings' });
  }, []);

  // Логирование загрузки настроек
  useEffect(() => {
    if (!isLoading && rules) {
      logger.info('bot_settings_loaded', { 
        rules_count: rules.length,
        tenant_id: effectiveTenantId 
      });
    }
  }, [isLoading, rules, effectiveTenantId]);
  
  return (
    <MainLayout headerTitle="Настройки Telegram бота" disableMainScroll>
      <LinearScreen
        eyebrow="Бот"
        title="Автоответы"
        lead="Правила, которыми бот отвечает в чате. Это не шаблоны рассылок."
        actions={
          <Button
            onClick={() => setEditingRule({
              id: '', name: '', triggers: [], is_regex: false,
              response: '', is_active: true, priority: 50,
            })}
            testId="btn-add-auto-reply"
            disabled={!effectiveTenantId}
          >
            <Plus className="mr-2 h-4 w-4" />
            Добавить автоответ
          </Button>
        }
        toolbar={
          isSuperAdmin ? (
            <div className="max-w-xs space-y-2">
              <TenantSelector
                value={selectedTenantId || ''}
                onChange={setSelectedTenant}
                required
              />
              {requireTenantSelection && (
                <p className={linearCanvas.meta}>Выберите клинику для просмотра автоответов.</p>
              )}
            </div>
          ) : undefined
        }
      >
      <div className="mx-auto max-w-4xl">
        
        {/* Список автоответов */}
        <div className="space-y-4" data-testid="auto-reply-list">
          {requireTenantSelection ? (
            <EmptyState
              type="noData"
              title="Выберите клинику"
              description="Выберите клинику для просмотра автоответов"
            />
          ) : isLoading ? (
            <LoadingState />
          ) : rules.length === 0 ? (
            <EmptyState 
              type="noData"
              title="Нет автоответов"
              description="Создайте первое правило"
              actionText="Добавить автоответ"
              onAction={() => setEditingRule({ 
                id: '', name: '', triggers: [], is_regex: false, 
                response: '', is_active: true, priority: 50 
              })}
            />
          ) : (
            rules.map((rule: AutoReplyRule) => (
              <Card key={rule.id} className="flex items-start gap-4">
                <GripVertical className="w-5 h-5 text-neutral-400 mt-1 cursor-move" />
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium">{rule.name}</span>
                    <Badge variant={rule.is_active ? 'success' : 'secondary'}>
                      {rule.is_active ? 'Активно' : 'Выключено'}
                    </Badge>
                    <span className="text-xs text-neutral-400">
                      Приоритет: {rule.priority}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mb-2">
                    {rule.triggers.map((trigger, i) => (
                      <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                        {rule.is_regex ? '/' : ''}{trigger}{rule.is_regex ? '/' : ''}
                      </span>
                    ))}
                  </div>
                  
                  <p className="text-sm text-neutral-600 whitespace-pre-wrap">
                    {rule.response}
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <button 
                    onClick={() => setEditingRule(rule)} 
                    className="p-2 hover:bg-neutral-100 rounded"
                    data-testid={`btn-edit-auto-reply-${rule.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => deleteMutation.mutate(rule.id)} 
                    className="p-2 hover:bg-red-100 rounded text-red-600"
                    data-testid={`btn-delete-auto-reply-${rule.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </Card>
            ))
          )}
        </div>
        
        {/* Modal для редактирования */}
        {editingRule && (
          <AutoReplyModal
            rule={editingRule}
            onSave={(rule) => saveMutation.mutate(rule)}
            onClose={() => setEditingRule(null)}
            isLoading={saveMutation.isPending}
          />
        )}
      </div>
      </LinearScreen>
    </MainLayout>
  );
}

